/**
 * How one outbox item goes on the wire, and which reads it changes.
 *
 * The mapping is the table in `@repo/client/offline`'s `outbox.ts` header:
 * `entityId` and `dependsOn` give the path, the body is the contract input the
 * enqueuer already built (with its client id). Bodies are passed as they were
 * stored — the server validates, and a 422 quarantines the item where the
 * person can see it.
 */
import { ApiError } from "@repo/client/api";
import type { AuthorizedFetcher, QueryKey } from "@repo/client/data/core";
import {
  createExerciseGoal,
  createExerciseGoalMutation,
  putExerciseNote,
  putExerciseNoteMutation,
  putExercisePreference,
  putExercisePreferenceMutation,
  removeExerciseGoal,
  removeExerciseGoalMutation,
  removeExerciseNote,
  removeExerciseNoteMutation,
} from "@repo/client/data/exercise";
import {
  meKeys,
  setDisplayName,
  setDisplayNameMutation,
  setLocale,
  setLocaleMutation,
  setTimeZone,
  setWeightUnit,
} from "@repo/client/data/me";
import {
  notificationPreferencesMutation,
  putNotificationPreferences,
} from "@repo/client/data/notifications";
import {
  addExclusion,
  addExclusionMutation,
  addPlanItem,
  addPlanItemMutation,
  addPlanSession,
  addPlanSessionMutation,
  dismissPlanSuggestion,
  movePlanItem,
  movePlanItemMutation,
  planKeys,
  removeExclusion,
  removeExclusionMutation,
  removePlanItem,
  removePlanItemMutation,
  removePlanSession,
  removePlanSessionMutation,
  reorderPlanSessions,
  reorderPlanSessionsMutation,
  replacePlanItem,
  replacePlanItemMutation,
  restorePlanItem,
  restorePlanItemMutation,
  retargetPlanItem,
  retargetPlanItemMutation,
  setPlanSuperset,
  setPlanSupersetMutation,
  updatePlanSession,
  updatePlanSessionMutation,
} from "@repo/client/data/plan";
import {
  deleteMeasurement,
  deleteMeasurementMutation,
  equipmentMutation,
  injuriesMutation,
  parqMutation,
  profileMutation,
  putEquipment,
  putInjuries,
  putParq,
  putProfile,
  putTrainingConfig,
  recordMeasurement,
  recordMeasurementMutation,
  switchGym,
  switchGymMutation,
  trainingConfigMutation,
} from "@repo/client/data/profile";
import {
  correctSet,
  correctSetMutation,
  endSession,
  endSessionMutation,
  recordSet,
  recordSetMutation,
  removeSet,
  removeSetMutation,
  sessionFeedbackMutation,
  sorenessMutation,
  startSession,
  startSessionMutation,
  submitSessionFeedback,
  submitSoreness,
} from "@repo/client/data/workout";
import {
  type OutboxItem,
  type OutboxKind,
  parseRetryAfter,
} from "@repo/client/offline";

type StartBody = Parameters<typeof startSession>[1];
type EndBody = Parameters<typeof endSession>[2];
type RecordBody = Parameters<typeof recordSet>[2];
type CorrectBody = Parameters<typeof correctSet>[2];

/**
 * What a `measurement.delete` that was queued before its add synced needs:
 * the server's id for the row, which only the add's answer carries (`D194`).
 * The runner's `onSent` records it here; the delete rides the same stream as
 * the add, so by the time it is sent the add has answered. Kept in memory and
 * in the item: a delete whose add answered in an earlier run of the app finds
 * the id in its own body, written there when the person undid.
 */
export interface MeasurementIds {
  get(idempotencyKey: string): string | undefined;
  set(idempotencyKey: string, measurementId: string): void;
}

export function createMeasurementIds(): MeasurementIds {
  const ids = new Map<string, string>();
  return {
    get: (key) => ids.get(key),
    set: (key, id) => {
      ids.set(key, id);
    },
  };
}

/** The process's one map; `provider.tsx` records into it from `onSent`. */
export const measurementIds = createMeasurementIds();

/**
 * Whether the server CREATED each exclusion an `exclusion.add` asked for —
 * its 201 against its 200 (`addExclusion`). An undo takes an exclusion back
 * only when this write made it, never an older choice of the person's; the
 * answer is recorded here when the write lands (`provider.tsx`), and until
 * then the undo reads what the device's cached refusal list said.
 */
export const exclusionAnswers = new Map<string, boolean>();

function bodyOf<T>(item: OutboxItem): T {
  return item.body as T;
}

function measurementIdOf(item: OutboxItem, ids: MeasurementIds): string {
  const own = (item.body as { measurementId?: unknown } | undefined)
    ?.measurementId;
  if (typeof own === "string" && own !== "") {
    return own;
  }
  const answered = ids.get(item.entityId);
  if (answered === undefined) {
    // Thrown as a plain Error so `classify` quarantines it where the person
    // can see it, rather than retrying a delete that names no row forever.
    throw new Error(
      `measurement.delete ${item.id}: the add ${item.entityId} never answered with an id`
    );
  }
  return answered;
}

/** The account's kinds (`D194`): one route call each, the body as stored. */
function sendAccountItem(
  api: AuthorizedFetcher,
  item: OutboxItem,
  ids: MeasurementIds
): Promise<unknown> {
  switch (item.kind) {
    case "session.soreness":
      return submitSoreness(api, item.entityId, bodyOf(item));
    case "session.feedback":
      return submitSessionFeedback(api, item.entityId, bodyOf(item));
    case "profile.put":
      return putProfile(api, bodyOf(item));
    case "training-config.put":
      return putTrainingConfig(api, bodyOf(item));
    case "equipment.put":
      return putEquipment(api, bodyOf(item));
    case "injuries.put":
      return putInjuries(api, bodyOf(item));
    case "parq.put":
      return putParq(api, bodyOf(item));
    case "notification-prefs.put":
      return putNotificationPreferences(api, bodyOf(item));
    case "me.locale.put":
      return setLocale(api, bodyOf(item));
    case "me.display-name.put":
      return setDisplayName(api, bodyOf(item));
    case "me.weight-unit.put":
      return setWeightUnit(api, bodyOf(item));
    case "me.time-zone.put":
      return setTimeZone(api, bodyOf(item));
    case "measurement.add":
      return recordMeasurement(api, bodyOf(item));
    case "measurement.delete":
      return deleteMeasurement(api, measurementIdOf(item, ids));
    case "exclusion.add":
      return addExclusion(api, bodyOf(item));
    case "exclusion.remove":
      return removeExclusion(api, item.entityId);
    // The person's own exercise data (`D200`): each answers a resend the way
    // it answered the send — a PUT replaces, a DELETE is a 204 again, a goal
    // under its client id is the stored goal.
    case "exercise-note.put":
      return putExerciseNote(api, item.entityId, bodyOf(item));
    case "exercise-note.remove":
      return removeExerciseNote(api, item.entityId);
    case "exercise-preference.put":
      return putExercisePreference(api, item.entityId, bodyOf(item));
    case "exercise-goal.add":
      return createExerciseGoal(api, bodyOf(item));
    case "exercise-goal.remove":
      return removeExerciseGoal(api, item.entityId);
    case "plan.suggestion.dismiss":
      return dismissPlanSuggestion(api);
    case "gym.use":
      return switchGym(api, bodyOf<{ gymId: string }>(item).gymId);
    case "plan.session.update":
      return updatePlanSession(api, {
        ...bodyOf<object>(item),
        sessionId: item.entityId,
      });
    case "plan.item.add":
      return addPlanItem(api, bodyOf(item));
    case "plan.item.replace":
      return replacePlanItem(api, {
        ...bodyOf<{ exerciseId: string; id: string; planId: string }>(item),
        itemId: item.entityId,
      });
    case "plan.item.remove":
      return removePlanItem(api, item.entityId);
    case "plan.item.restore":
      return restorePlanItem(api, {
        ...bodyOf<{ planId: string; replacing?: string }>(item),
        itemId: item.entityId,
      });
    case "plan.item.retarget":
      return retargetPlanItem(api, {
        ...bodyOf<{ id: string; planId: string }>(item),
        itemId: item.entityId,
      });
    case "plan.session.add":
      return addPlanSession(
        api,
        bodyOf<{ id: string; label: string; planId: string }>(item)
      );
    case "plan.session.remove":
      return removePlanSession(api, item.entityId).catch(dayAlreadyGone);
    case "plan.item.move":
      return movePlanItem(api, {
        ...bodyOf<{ id: string; planId: string; sessionId: string }>(item),
        itemId: item.entityId,
      });
    case "plan.sessions.reorder":
      return reorderPlanSessions(
        api,
        bodyOf<{ planId: string; sessionIds: string[] }>(item)
      ).catch(orderOvertaken);
    case "plan.superset":
      return setPlanSuperset(
        api,
        bodyOf<{ grouped: boolean; itemIds: string[]; planId: string }>(item)
      ).catch(groupingOvertaken);
    default:
      throw new Error(`unknown outbox kind ${item.kind}`);
  }
}

/**
 * A removal that finds the day no longer on the plan has done its work
 * (`D197`): only a removal takes a day off, so the resend after a lost
 * answer — or the trainer's removal of the same day — leaves the person's
 * decision standing. The server cannot tell that case from a day that never
 * existed, because a removed day is not loaded; the day id came from the
 * plan this device read, so here it can.
 */
function dayAlreadyGone(error: unknown): void {
  if (
    error instanceof ApiError &&
    error.status === 404 &&
    error.messageKey === "plan.session_not_found"
  ) {
    return;
  }
  throw error;
}

/**
 * A reorder whose list no longer matches the plan's days has been overtaken
 * (`D210`): a day was added or removed after it was queued — by this device
 * later in the stream, by the trainer, or on another phone — and the order
 * the person chose then is not one the plan can take now. Quarantining it
 * would ask them to resolve a move the screen has already redrawn; it is
 * dropped as done, and the plan shows the order the server holds.
 */
function orderOvertaken(error: unknown): void {
  if (
    error instanceof ApiError &&
    error.status === 422 &&
    error.messageKey === "plan.reorder_sessions_incomplete"
  ) {
    return;
  }
  throw error;
}

/**
 * The refusals of a superset write decided against a day that has changed
 * since (`D246`): a line removed or moved, by this device later in the
 * stream, by the trainer, or on another phone, so the lines named no longer
 * stand together. The grouping the person chose is not one the day can take
 * now, and the screen already draws the server's day; it is dropped as done,
 * as an overtaken reorder is, rather than quarantined.
 */
const SUPERSET_OVERTAKEN: ReadonlySet<string> = new Set([
  "plan.item_not_found",
  "plan.superset_mixed_days",
  "plan.superset_not_consecutive",
]);

function groupingOvertaken(error: unknown): void {
  if (
    error instanceof ApiError &&
    error.status === 422 &&
    SUPERSET_OVERTAKEN.has(error.messageKey)
  ) {
    return;
  }
  throw error;
}

function sessionOf(item: OutboxItem): string {
  if (item.dependsOn === null) {
    // `enqueue` refuses this for every kind but the start; reaching here is a
    // corrupt item. Thrown as a plain Error, so `classify` quarantines it.
    throw new Error(`outbox ${item.kind} ${item.id} has no session`);
  }
  return item.dependsOn;
}

function sendItem(
  api: AuthorizedFetcher,
  item: OutboxItem,
  ids: MeasurementIds
): Promise<unknown> {
  switch (item.kind) {
    case "session.start":
      return startSession(api, item.body as StartBody);
    case "session.end":
      return endSession(api, item.entityId, (item.body ?? {}) as EndBody);
    case "set.record":
      return recordSet(api, sessionOf(item), item.body as RecordBody);
    case "set.correct":
      return correctSet(api, item.entityId, item.body as CorrectBody);
    case "set.delete":
      return removeSet(api, item.entityId);
    default:
      return sendAccountItem(api, item, ids);
  }
}

/**
 * `send` for the runner, over the app's `authorizedFetch`.
 *
 * A 429's `Retry-After` is attached to the thrown `ApiError` as
 * `retryAfterMs`: `ApiError` keeps no headers, and `classify` (T21) reads the
 * floor from that property. The header is caught on the way past, from the
 * response the route function is about to turn into the error.
 */
export function createOutboxSend(
  api: AuthorizedFetcher,
  now: () => Date = () => new Date(),
  ids: MeasurementIds = measurementIds
): (item: OutboxItem) => Promise<unknown> {
  return async (item) => {
    let retryAfter: string | null = null;
    const watching: AuthorizedFetcher = {
      authorizedFetch: async (path, init) => {
        const response = await api.authorizedFetch(path, init);
        if (response.status === 429) {
          retryAfter = response.headers.get("retry-after");
        }
        return response;
      },
    };
    try {
      return await sendItem(watching, item, ids);
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        const retryAfterMs = parseRetryAfter(retryAfter, now());
        if (retryAfterMs !== undefined) {
          Object.assign(error, { retryAfterMs });
        }
      }
      throw error;
    }
  };
}

/**
 * The reads each kind changes, taken from the SAME `MutationDef`s the data
 * layer declares (T19), so the outbox and a direct mutation can never
 * disagree about what a set write invalidates. The api is only captured by
 * `mutationFn`, which is never called here.
 */
export function outboxInvalidations(
  api: AuthorizedFetcher
): (kind: OutboxKind) => readonly QueryKey[] {
  const of = (def: { readonly invalidates?: readonly QueryKey[] }) =>
    def.invalidates ?? [];
  const table: Record<OutboxKind, readonly QueryKey[]> = {
    "equipment.put": of(equipmentMutation(api)),
    "exclusion.add": of(addExclusionMutation(api)),
    "exclusion.remove": of(removeExclusionMutation(api)),
    "exercise-goal.add": of(createExerciseGoalMutation(api)),
    "exercise-goal.remove": of(removeExerciseGoalMutation(api)),
    "exercise-note.put": of(putExerciseNoteMutation(api)),
    "exercise-note.remove": of(removeExerciseNoteMutation(api)),
    "exercise-preference.put": of(putExercisePreferenceMutation(api)),
    "injuries.put": of(injuriesMutation(api)),
    "me.display-name.put": of(setDisplayNameMutation(api)),
    "me.locale.put": of(setLocaleMutation(api)),
    // The zone moves nothing a screen reads but the account itself.
    "me.time-zone.put": [meKeys.all],
    // `D266`. The unit is read off `me`; nothing else stores it.
    "me.weight-unit.put": [meKeys.all],
    "measurement.add": of(recordMeasurementMutation(api)),
    "measurement.delete": of(deleteMeasurementMutation(api)),
    "notification-prefs.put": of(notificationPreferencesMutation(api)),
    "parq.put": of(parqMutation(api)),
    "plan.item.add": of(addPlanItemMutation(api)),
    "plan.item.remove": of(removePlanItemMutation(api)),
    "plan.item.replace": of(replacePlanItemMutation(api)),
    "plan.item.restore": of(restorePlanItemMutation(api)),
    "plan.item.retarget": of(retargetPlanItemMutation(api)),
    "plan.session.add": of(addPlanSessionMutation(api)),
    "plan.session.remove": of(removePlanSessionMutation(api)),
    "plan.session.update": of(updatePlanSessionMutation(api)),
    "plan.sessions.reorder": of(reorderPlanSessionsMutation(api)),
    "plan.item.move": of(movePlanItemMutation(api)),
    "plan.superset": of(setPlanSupersetMutation(api)),
    // What `suggestion-card.tsx` invalidated after its direct call.
    "plan.suggestion.dismiss": [planKeys.activeAll],
    "gym.use": of(switchGymMutation(api)),
    "profile.put": of(profileMutation(api)),
    "session.end": of(endSessionMutation(api)),
    "session.feedback": of(sessionFeedbackMutation(api)),
    "session.soreness": of(sorenessMutation(api)),
    "session.start": of(startSessionMutation(api)),
    "set.correct": of(correctSetMutation(api)),
    "set.delete": of(removeSetMutation(api)),
    "set.record": of(recordSetMutation(api)),
    "training-config.put": of(trainingConfigMutation(api)),
  };
  return (kind) => table[kind];
}
